import confetti from 'canvas-confetti'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import './fiestasPatrias.css'

import { BLUE, CHOREOGRAPHY_CSS, RED, SCENE, SCENE_WIDTH, STRAW, WHITE } from './scene'

import type { AnnouncementProps } from '../types'

/*
A greeting, not a release: no journey, no simulator, no story column. The
card is the Fiestas Patrias design the stakeholders reviewed —the flag's own
geometry as layout, the couple dancing cueca on the fonda floor— laid out in
its 1100x576 pixels and scaled as one piece to whatever width the modal has.

Decorative markup comes from scene.ts as strings (see the note there).
*/

const BURST_MS = 1500

// Real confetti from the pressed button, in the flag's colours, over the modal.
// Not gated on prefers-reduced-motion: this only ever runs from a click, and
// the person clicking "¡Viva Chile!" is asking for exactly this.
const fiesta = (from: HTMLElement) => {
  const rect = from.getBoundingClientRect()
  const origin = {
    x: (rect.left + rect.width / 2) / window.innerWidth,
    y: (rect.top + rect.height / 2) / window.innerHeight,
  }
  const colors = [RED, WHITE, BLUE, STRAW]

  // Radix renders the dialog in a portal at the end of <body>; the confetti
  // canvas needs to sit above it or the celebration happens behind the card.
  void confetti({ particleCount: 90, spread: 70, startVelocity: 45, origin, colors, zIndex: 10000 })
  void confetti({ particleCount: 60, spread: 120, startVelocity: 30, scalar: 0.8, origin, colors, zIndex: 10000 })
}

export const Body = ({ onDismiss }: AnnouncementProps) => {
  const frameRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  const [bursting, setBursting] = useState(false)
  const burstTimer = useRef<ReturnType<typeof setTimeout>>()

  // Scale is measured, not media-queried: the card is 1100px wide on a
  // desktop and whatever is left of the viewport on a phone, and the arrows
  // beside it take a share too.
  useLayoutEffect(() => {
    const frame = frameRef.current

    if (!frame) return

    const measure = () => setScale(frame.clientWidth / SCENE_WIDTH)
    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(frame)

    return () => observer.disconnect()
  }, [])

  useEffect(() => () => clearTimeout(burstTimer.current), [])

  // Re-clicking restarts the burst: drop the class, let a frame pass, add it.
  const viva = (event: React.MouseEvent<HTMLElement>) => {
    fiesta(event.currentTarget)
    setBursting(false)
    clearTimeout(burstTimer.current)
    requestAnimationFrame(() => {
      setBursting(true)
      burstTimer.current = setTimeout(() => setBursting(false), BURST_MS)
    })
  }

  return (
    <div ref={frameRef} className="fp-frame">
      <style>{CHOREOGRAPHY_CSS}</style>

      <div className={`fp-scene${bursting ? ' is-burst' : ''}`} style={{ '--fp-scale': scale } as React.CSSProperties}>
        <div className="fp-canton fp-rise" style={{ animationDelay: '80ms' }}>
          <div style={{ position: 'absolute', left: 44, top: 78, width: 56, height: 56 }}>
            <span dangerouslySetInnerHTML={{ __html: SCENE.star }} />
            <span className="fp-glint" style={{ left: -10, top: 6, animationDelay: '0.4s' }} />
            <span className="fp-glint" style={{ left: 58, top: 40, animationDelay: '1.6s' }} />
            <span className="fp-glint" style={{ left: 30, top: -12, width: 4, height: 4, animationDelay: '2.4s' }} />
          </div>

          <div
            style={{
              position: 'absolute',
              left: 44,
              top: 156,
              width: 352,
              display: 'flex',
              flexDirection: 'column',
              gap: 18,
            }}
          >
            <p
              className="fp-mono fp-rise"
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.16em',
                color: 'rgba(255, 255, 255, 0.72)',
                animationDelay: '260ms',
              }}
            >
              18 · 19 de septiembre · Chile
            </p>
            <h2 className="fp-headline fp-rise" style={{ animationDelay: '380ms' }}>
              ¡Felices
              <br />
              Fiestas
              <br />
              Patrias!
            </h2>
            <p
              className="fp-rise"
              style={{
                maxWidth: 340,
                fontSize: 15,
                lineHeight: 1.5,
                color: 'rgba(255, 255, 255, 0.86)',
                animationDelay: '520ms',
              }}
            >
              Les deseamos un Dieciocho con buena mesa, buena cueca y muy buenas ventas.
            </p>
            <div
              className="fp-rise"
              style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 6, animationDelay: '660ms' }}
            >
              <button type="button" className="fp-cta" onClick={viva}>
                <span style={{ display: 'contents' }} dangerouslySetInnerHTML={{ __html: SCENE.ctaStar }} />
                ¡Viva Chile!
              </button>
              <button
                type="button"
                className="fp-ghost"
                onClick={(event) => {
                  fiesta(event.currentTarget)
                  onDismiss()
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>

        <div className="fp-band fp-rise" style={{ animationDelay: '200ms' }}>
          <div style={{ position: 'absolute', left: 44, bottom: 22, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ display: 'block', width: 28, height: 1, background: 'rgba(255, 255, 255, 0.5)' }} />
            <p
              className="fp-mono"
              style={{ fontSize: 10, letterSpacing: '0.14em', color: 'rgba(255, 255, 255, 0.78)' }}
            >
              Veekls · Santiago de Chile · Septiembre 2026
            </p>
          </div>
        </div>

        <div
          className="fp-decor fp-rise"
          style={{ inset: 0, animationDelay: '500ms' }}
          dangerouslySetInnerHTML={{ __html: SCENE.kites }}
        />

        {/* The cueca: both dancers pivot around the spot at (770, 480). */}
        <div
          className="fp-stage fp-rise"
          style={{ left: 660, top: 188, width: 220, height: 300, animationDelay: '420ms' }}
        >
          <span className="fp-shadow fp-shadow-h" style={{ width: 130, height: 14, left: 45, top: 280 }} />
          <span className="fp-shadow fp-shadow-c" style={{ width: 150, height: 14, left: 35, top: 280 }} />
          <div className="fp-spot fp-spot-h" dangerouslySetInnerHTML={{ __html: SCENE.huaso }} />
          <div className="fp-spot fp-spot-c" dangerouslySetInnerHTML={{ __html: SCENE.china }} />
        </div>

        <div
          className="fp-decor fp-drop"
          style={{ left: 0, top: 0, width: 1100, height: 96, animationDelay: '120ms' }}
          dangerouslySetInnerHTML={{ __html: SCENE.bunting }}
        />

        <div className="fp-confetti" dangerouslySetInnerHTML={{ __html: SCENE.confetti }} />

        {/* Burst anchored on the CTA */}
        <div className="fp-burst" style={{ left: 128, top: 470 }} dangerouslySetInnerHTML={{ __html: SCENE.burst }} />
      </div>
    </div>
  )
}
